import {
  InteractionStepType,
  AssistantContent,
  ModelResponseSelectorStepResponse,
  MessageRole,
  InteractionStepConfig,
  IndexedInteractionStepResponse,
  EXISTING_CANDIDATE_ID,
  StepParamsForType,
  StepParamsForTypeWithStage,
  PREVIOUS_TURN_CANDIDATE_ID,
} from '@scale/shared/types/chat';
import _ from 'lodash';
import { buildCandidatesWithId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils';
import { maybeEnsureResponseSelfConsistency } from '@scale/shared/chat/response';

import { extractPrefilledMessages } from '@scale/shared/chat/response/validation';

import { ModelResponseSelectorConfigurationComponent } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ModelResponseSelector/ModelResponseSelectorConfigurationComponent';
import { fetchCandidateForEndpoint } from 'react-scale/hooks/chat/useChatCompletion';
import { CandidateMessage, CandidateState, UserMessage } from '@scale/shared/types/chat-completion';
import { Types } from '@scale/scaleui';
import { IMultiTurnThreadHistory } from '../../ThreadHistory.interface';
import { isConditionallyVisible, isStepVisible } from '@scale/shared/chat/api';
import { ReviewLevel } from '@scale/shared-types';
import { ResponseTemplatePath } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseTemplateCreator/ResponseTemplatePath';

// This is to ensure that skip queries are able to be precached during the prefetch stage
const SkippedQueryCache: { [key: string]: string } = {};

import { StepDescriptor } from '../stepDescriptor/StepDescriptor';
import { registerStepDescriptor } from '../stepDescriptor/stepDescriptorContainer';
import { schema } from './validation';
import ModelResponseSelectorViewComponent from './ModelResponseSelectorViewComponent';
import ModelResponseSelectorPinnedViewComponent from './ModelResponseSelectorPinnedViewComponent';
import logger from '@scale/frontend-lib/logging';

export class ModelResponseSelectorDescriptor extends StepDescriptor<InteractionStepType.ModelResponseSelector> {
  type = InteractionStepType.ModelResponseSelector;

  title = InteractionStepConfig[InteractionStepType.ModelResponseSelector].title;
  description = InteractionStepConfig[InteractionStepType.ModelResponseSelector].description;

  validationSchema = schema;

  ViewComponent = ModelResponseSelectorViewComponent;
  PinnedViewComponent = ModelResponseSelectorPinnedViewComponent;
  ConfigurationComponent = ModelResponseSelectorConfigurationComponent;

  isResponseValid(
    response: IndexedInteractionStepResponse,
    stepParams: StepParamsForTypeWithStage<InteractionStepType.ModelResponseSelector>,
    history: IMultiTurnThreadHistory,
    reviewLevel?: ReviewLevel,
  ) {
    if (
      !isStepVisible(stepParams, reviewLevel) ||
      !isConditionallyVisible(
        stepParams,
        history.responses,
        response.index,
        history.threadParams.templateVariables,
        reviewLevel,
      )
    ) {
      return { canSubmit: true, submitTooltip: '' };
    }

    if (!!stepParams.params.show_objectivity && _.isNil(response.context.isObjective)) {
      return {
        canSubmit: false,
        submitTooltip:
          'Please respond to whether or not others would agree with you on your model selection.',
      };
    }

    return { canSubmit: true, submitTooltip: '' };
  }

  async doPrefetch(
    params: StepParamsForType<InteractionStepType.ModelResponseSelector>,
    history: IMultiTurnThreadHistory,
  ) {
    await this.loadSkipContext(params, history);
  }

  requiresSkipContext(params: StepParamsForType<InteractionStepType.ModelResponseSelector>) {
    return (
      !!params.params.force_consistent_responses &&
      !!params.params.visibility_restricted?.includes(ReviewLevel.ReviewConsensus)
    );
  }

  async loadSkipContext(
    params: StepParamsForType<InteractionStepType.ModelResponseSelector>,
    history: IMultiTurnThreadHistory,
  ) {
    if (!params.params.force_consistent_responses) {
      return;
    }
    const historyChopped = history.getChatHistory().slice(0, -1);

    if (history.threadParams?.systemPrompt?.length) {
      const hasGptModel = (params?.params?.chat_models ?? []).some(chat_model =>
        (chat_model.id ?? '').includes('gpt'),
      );
      if (!history?.threadParams?.systemPromptGptOnly || hasGptModel) {
        historyChopped.push({
          role: MessageRole.System,
          content: history.threadParams.systemPrompt,
        });
      }
    }

    const chatPrompt = history.getMostRecentPrompt();
    const attachments = history.getMostRecentPromptAttachments();
    const userMessage: UserMessage = {
      role: MessageRole.User,
      content: chatPrompt,
      ...(attachments.length > 0 ? { attachments } : {}),
    };
    const chatApiEndpoints = params.params.chat_models;
    const candidatesIndexes = _.range(chatApiEndpoints.length);
    const candidates = await Promise.all(
      candidatesIndexes.map(async candidateIndex => {
        const response = await fetchCandidateForEndpoint(
          chatApiEndpoints,
          candidateIndex,
          userMessage,
          historyChopped,
          false, // shouldCorruptMessage
          'chat-main model-response-selector fetch-candidate-for-endpoint', // useCase
          undefined, // chatBenchmarkConfig
          SkippedQueryCache,
        );
        const candidate: CandidateMessage = {
          index: candidateIndex,
          state: CandidateState.Completed,
          message: {
            role: MessageRole.Assistant,
            content: response,
          },
          sourceId: chatApiEndpoints[candidateIndex].id,
        };
        return candidate;
      }),
    );
    return { candidates };
  }

  // In order for templating to work correctly with pre-existing responses, the order of this needs to be the same as the natural order of the candidates
  getCandidatesInOrder(
    stepParams: StepParamsForType<InteractionStepType.ModelResponseSelector>,
    history: IMultiTurnThreadHistory,
  ): {
    candidates: CandidateMessage[];
    skipTemplating: boolean;
  } {
    let { initialMessages } = history.threadParams;
    const { skip_initial_messages } = stepParams.params;
    initialMessages = skip_initial_messages ? [] : initialMessages;
    const { taskingAssistantMessages } = extractPrefilledMessages(initialMessages);
    const turnIndex = history.getNumTurns() - 1;
    let assistantProvidedMessages: AssistantContent[] = [];
    if (turnIndex < taskingAssistantMessages.length) {
      assistantProvidedMessages = taskingAssistantMessages[turnIndex].options ?? [];
    }
    let index = 0;
    const { params } = stepParams;
    const candidates: CandidateMessage[] = [];

    if (params.use_last_model_responses) {
      const lastModelResponse = history.getMostRecentResponseForStepType(
        InteractionStepType.ModelResponseSelector,
      );
      return { candidates: lastModelResponse?.context.candidates ?? [], skipTemplating: true };
    }

    /*
     * The order should be as follows (matching the MRS logic):
     * 1. Chat models
     * 2. Provided model responses (deprecated)
     * 3. Assistant initial messages
     * 4. Previous turn model response (for RLHF-RTL)
     * 5. Most recent model response
     */
    if (params.chat_models) {
      candidates.push(
        ...(params.chat_models.map(model => {
          return {
            index: index++,
            state: CandidateState.Completed,
            message: {
              role: MessageRole.Assistant as const,
              content: '',
            },
            sourceId: model.id,
          };
        }) as CandidateMessage[]),
      );
    }

    if (params.provided_model_responses) {
      candidates.push(
        ...(params.provided_model_responses.map(response => {
          return {
            index: index++,
            state: CandidateState.Completed,
            message: {
              role: MessageRole.Assistant,
              content: response.content,
              attachments: response?.attachments,
            },
            sourceId: response.id,
          };
        }) as CandidateMessage[]),
      );
    }

    if (assistantProvidedMessages) {
      candidates.push(
        ...(assistantProvidedMessages.map(message => {
          return {
            index: index++,
            state: CandidateState.Completed,
            message: {
              role: MessageRole.Assistant,
              content: message.content,
              attachments: message?.attachments,
            },
            sourceId: message.id,
          };
        }) as CandidateMessage[]),
      );
    }

    // TODO (matthew / allen): For RLHF-RTL, deprecate when not in use
    const latestModelResponseInPreviousTurn = history.useMostRecentResponseInTurn(
      turnIndex,
      stepParams,
    );
    if (params.use_previous_turn_model_response_as_option && latestModelResponseInPreviousTurn) {
      candidates.push({
        index: index++,
        state: CandidateState.Completed,
        message: {
          role: MessageRole.Assistant,
          content: latestModelResponseInPreviousTurn,
        },
        sourceId: PREVIOUS_TURN_CANDIDATE_ID,
      });
    }

    if (params.use_existing_model_response_as_option) {
      if (params.include_all_responses_from_most_recent_response) {
        logger.info(
          'Detected getCandidatesInOrder called with use_existing_model_response_as_option and include_all_responses_from_most_recent_response',
          {
            stepParams: JSON.stringify(stepParams),
          },
        );
      }
      candidates.push({
        index: index++,
        state: CandidateState.Completed,
        message: {
          role: MessageRole.Assistant,
          content: '',
        },
        sourceId: EXISTING_CANDIDATE_ID,
      });
    }

    return { candidates, skipTemplating: false };
  }

  // Avoid passing in the full response to maintain the abstraction
  /**
   * @param displayOrder Used for correcting the order of the responses in the editor
   */
  getResponseTemplatePaths(
    stepParams: StepParamsForType<InteractionStepType.ModelResponseSelector>,
    history: IMultiTurnThreadHistory,
    displayOrder?: number[],
  ) {
    const { params } = stepParams;
    const { candidates, skipTemplating } = this.getCandidatesInOrder(stepParams, history);
    const defaultDisplayOrder = _.range(candidates.length);
    const normalizedDisplayOrder = displayOrder ?? defaultDisplayOrder;

    const responseTemplatePaths: ResponseTemplatePath[] = [];

    // When use_last_model_responses is enabled, we don't need to template the candidates, but we need them for the selectedIndex
    const candidateMessages = !skipTemplating
      ? normalizedDisplayOrder.map((displayOrderIndex, candidateIndex) => ({
          path: `context.candidates[${displayOrderIndex}].message.content`,
          label: `Response ${candidateIndex + 1}: ${candidates[displayOrderIndex].sourceId}`,
          hideFromAnswerKey: true,
        }))
      : [];

    responseTemplatePaths.push(...candidateMessages);

    if (!params.skip_selection) {
      const selectedIndex = {
        path: 'context.selectedIndex',
        label: `Selected Index`,
        expectedValues: _.range(candidates.length),
        parseAsPrimitive: true,
        fieldId: 'selectedIndex',
      };

      responseTemplatePaths.push(selectedIndex);
    }

    if (params.show_likert_scale) {
      const maxLikert = params.max_likert || 7;
      const minLikert = params.min_likert || 1;
      const likertScale = {
        path: 'context.likertValueRaw',
        label: `Likert Scale (${minLikert}-${maxLikert})`,
        expectedValues: _.range(minLikert, maxLikert + 1),
        parseAsPrimitive: true,
        fieldId: 'likert',
      };
      responseTemplatePaths.push(likertScale);
    }

    // if history.turn.params.show_objectivity, then push a new path for the objectivity
    if (params.show_objectivity) {
      const objectivity = {
        path: 'context.isObjective',
        label: `Objectivity`,
        expectedValues: ['true', 'false'],
        parseAsPrimitive: true,
        fieldId: 'objectivity',
      };
      responseTemplatePaths.push(objectivity);
    }

    if (params.show_justification) {
      const justification = {
        path: 'context.justificationRaw',
        label: `Justification`,
        fieldId: 'justificationRaw',
      };
      responseTemplatePaths.push(justification);
    }

    return responseTemplatePaths;
  }

  generateEmptyResponse(
    stepParams: StepParamsForType<InteractionStepType.ModelResponseSelector>,
    history: IMultiTurnThreadHistory,
  ) {
    const { candidates } = this.getCandidatesInOrder(stepParams, history);
    const chatMessages = history.getChatHistory();
    const emptyCandidates = buildCandidatesWithId(candidates, []);
    return {
      type: InteractionStepType.ModelResponseSelector,
      context: {
        messages: chatMessages,
        candidates: emptyCandidates,
        displayOrder: _.range(candidates.length),
        selectedIndex: 0,
        selectedId: emptyCandidates[0].id,
      },
      output: emptyCandidates[0].message.content,
    };
  }

  ensureResponseConsistency(
    stepParams: StepParamsForType<InteractionStepType.ModelResponseSelector>,
    history: IMultiTurnThreadHistory,
    response: ModelResponseSelectorStepResponse,
  ) {
    // If selectedIndex and selectedId don't agree, default to the selectedIndex
    maybeEnsureResponseSelfConsistency(stepParams, response);
    // Make sure Chat messages are in sync
    const chatMessages = history.getChatHistory();
    response.context.messages = chatMessages;
  }

  getConfigurationPreviewTags(
    stepParams: StepParamsForType<InteractionStepType.ModelResponseSelector>,
  ): Types.Tag[] {
    const tags = super.getConfigurationPreviewTags(stepParams);
    const { params } = stepParams;
    if (params.skip_selection) {
      tags.push({
        value: 'Hidden (Used to Load Responses)',
        variant: 'primary',
        icon: 'eye-slash',
      });
    }
    if (params.chat_models && params.chat_models.length) {
      for (const model of params.chat_models) {
        if (model.params && model.params.model) {
          tags.push({
            key: model.id,
            value: model.params.model,
            variant: 'success',
            icon: 'network-wired',
          });
        }
        if (model.projectModelConfigurationKey) {
          tags.push({
            key: model.id,
            value: model.projectModelConfigurationKey,
            variant: 'success',
            icon: 'network-wired',
          });
        }
      }
    } else if (!params.use_last_model_responses) {
      tags.push({
        value: 'No Endpoints Set',
        variant: 'warning',
        icon: 'circle-exclamation',
      });
    }
    if (params.use_existing_model_response_as_option) {
      tags.push({
        value: 'Use Most Recent Response',
        variant: 'success',
        icon: ['far', 'comment'],
      });
    }
    if (params.use_previous_turn_model_response_as_option) {
      tags.push({
        value: 'Use Previous Turn Response',
        variant: 'success',
        icon: ['far', 'comment'],
      });
    }
    if (params.use_last_model_responses) {
      tags.push({
        value: 'Use Previous Model Responses',
        variant: 'success',
        icon: ['far', 'diagram-previous'],
      });
    }
    return tags;
  }
}

registerStepDescriptor({
  type: InteractionStepType.ModelResponseSelector,
  descriptor: new ModelResponseSelectorDescriptor(),
});
